import React from 'react';
import {
  Bell,
  BrainCircuit,
  FileBadge,
  FileCheck,
  FileSignature,
  ScanText,
  Search,
  Users,
} from 'lucide-react';

interface Feature {
  status: 'available' | 'soon';
  title: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const features: Feature[] = [
  {
    status: 'available',
    title: 'Quesiti Fiscali e Normativi',
    description:
      'In pochi secondi ottieni un parere completo e argomentato, con giurisprudenza favorevole e contraria, riferimenti diretti e link alle fonti ufficiali. Una visione a 360° per decidere o difendere con sicurezza.',
    Icon: BrainCircuit,
  },
  {
    status: 'available',
    title: 'Analisi Approfondita di Casi',
    description:
      'Invia il tuo caso o una bozza di atto: Trovanorma analizza le fonti ufficiali, evidenzia criticità nascoste e suggerisce strategie concrete per rafforzare la tua posizione.',
    Icon: ScanText,
  },
  {
    status: 'available',
    title: 'Validazione di Contratti e Documenti',
    description:
      'Carica il documento e Trovanorma lo confronta con la normativa vigente, segnalando subito le parti a rischio o non in linea con la legge.',
    Icon: FileCheck,
  },
  {
    status: 'available',
    title: 'Creazione e Revisione di Documenti',
    description:
      'Trovanorma genera bozze su misura in base alle tue specifiche. Puoi modificarle, confrontare le versioni e arrivare alla stesura finale perfettamente conforme e pronta per l’uso.',
    Icon: FileSignature,
  },
  {
    status: 'soon',
    title: 'Ricerca e Sintesi Normativa',
    description:
      'Cerca trasversalmente tra tutte le fonti ufficiali italiane (Gazzetta Ufficiale, Cassazione civile e penale, ADE, INPS, INAIL e altre) e ricevi un riepilogo chiaro con i punti chiave già pronti per l’uso nei tuoi pareri.',
    Icon: Search,
  },
  {
    status: 'soon',
    title: 'Redazione Guidata di Atti',
    description:
      'Trovanorma genera bozze già allineate alla normativa vigente e personalizzate in base alle tue esigenze, riducendo drasticamente tempi e margini di errore.',
    Icon: FileBadge,
  },
  {
    status: 'soon',
    title: 'Alert Normativi e di Giurisprudenza',
    description:
      'Trovanorma ti avvisa automaticamente quando una norma, sentenza o prassi che hai consultato o salvato viene modificata, abrogata o integrata. Puoi impostare alert anche per campi di interesse specifici, così non ti sfugge mai nulla di rilevante per il tuo lavoro.',
    Icon: Bell,
  },
  {
    status: 'soon',
    title: 'Gestione Clienti e Pratiche',
    description:
      'Organizza anagrafiche, documenti e pareri per cliente direttamente in Trovanorma. Potrai così effettuare ricerche e generare documenti partendo da situazioni e materiali già esistenti, riducendo tempi e aumentando la precisione.',
    Icon: Users,
  },
];

const FeaturesSection = () => {
  return (
    <section id="features" className="py-16 px-4 md:px-8 bg-primary-50 scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6 px-4 font-funnel">
          Uno strumento, <span className="text-accent">tutte le risposte</span> di cui hai bisogno
        </h2>
        <p className="text-lg text-neutral-700 mb-12 px-4">
          Dalla risposta rapida alla validazione di contratti, fino alla redazione di atti completi.
          Trovanorma ti affianca in ogni fase del tuo lavoro, con precisione e fonti sempre
          aggiornate.
        </p>
        <div className="relative">
          <div
            className="md:block absolute md:left-1/2 top-0 h-full w-0.5 bg-neutral-400"
            aria-hidden="true"
          ></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 items-center">
            <div className="h-30 hidden md:block"></div>
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} feature={feature} index={index} />
            ))}
            <div className="h-30 hidden md:block"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface FeatureCardProps {
  feature: Feature;
  index: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, index }) => {
  const isLeft = index % 2 !== 0; // Gli elementi con indice dispari vanno a sinistra
  const isAvailable = feature.status === 'available';

  const tailBaseClasses = 'absolute w-3 h-3 transform rotate-45 top-1/2 -translate-y-1/2';
  let tailPositionClasses: string;
  let circlePositionClasses: string;
  if (!isLeft) {
    tailPositionClasses = `left-[-6px] md:left-[-6px]`;
    circlePositionClasses = 'md:-left-6 -translate-x-1/2';
  } else {
    tailPositionClasses = `left-[-6px] md:left-auto md:right-[-6px]`;
    circlePositionClasses = 'md:-right-6 -translate-x-1/2 md:translate-x-1/2';
  }
  const bubbleColor = isAvailable ? 'bg-white' : 'bg-neutral-50';

  return (
    <div className={`relative row-span-2 md:items-center`}>
      <div
        className={`relative mb-0 ml-6 md:ml-0 ${isLeft ? 'md:col-start-1 md:row-start-1' : 'md:col-start-2'}`}
      >
        <div
          className={`
          p-6 rounded-xl shadow-md border transition-shadow duration-300 hover:shadow-lg
          ${isAvailable ? 'bg-white border-neutral-200' : 'bg-neutral-50 border-neutral-200 opacity-90'}
        `}
        >
          <span
            className={`
            inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-3
            ${isAvailable ? 'bg-green-100 text-success-500' : 'bg-blue-100 text-blue-800'}
          `}
          >
            {isAvailable ? 'Disponibile Ora' : 'In Arrivo'}
          </span>

          <div className="flex items-start gap-4">
            <div>
              <h3 className="text-lg font-semibold text-text-base">
                <feature.Icon
                  className={`inline flex-shrink-0 -mt-1 mr-2 w-6 h-6 ${isAvailable ? 'text-accent' : 'text-neutral-500'}`}
                />
                {feature.title}
              </h3>
              <p className="mt-2 text-neutral-600 text-sm lg:text-base">{feature.description}</p>
            </div>
          </div>
        </div>
        <div className={`${tailBaseClasses} ${bubbleColor} ${tailPositionClasses}`}></div>
      </div>

      <div
        className={`${circlePositionClasses} md:flex absolute top-1/2 w-4 h-4 -translate-y-1/2 rounded-full bg-white border-2 ${isAvailable ? 'border-accent' : 'border-neutral-500'}`}
      ></div>
    </div>
  );
};

export default FeaturesSection;
